import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { AssetSelect, CreatePage, FuseInput, LoanSelect } from '/src/components';
import { useAdvanceStore, useAssetStore, useFilterStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { TAssetId } from '/src/lib/models';
import { CreateAdvanceForm, DataModelType, FormErrors } from '/src/lib/types';
import { currency, isNumber } from '/src/utils';

/**
 * Create Advance Page
 */
export const CreateAdvancePage: React.FC = observer(() => {
  /** Context **/
  const advanceStore = useAdvanceStore();
  const assetStore = useAssetStore();
  const filterStore = useFilterStore();
  const navigate = useNavigate();

  /** State **/
  const [isLoaded, setIsLoaded] = useState(false);
  const [formValues, setFormValues] = useState<CreateAdvanceForm>({
    name: '',
    amount: '',
    dateRequested: new Date(),
    originationFee: 0,
    effectiveDate: '',
    notes: [],
    files: [],
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  /** Computed **/
  const loans = useMemo(() => filterStore.loanListGet(), [filterStore._loanList]);

  const filteredLoans = loans;
  const filteredAssets = formValues.loan ? formValues.loan.assets.toSorted((a, b) => a.name.localeCompare(b.name)) : [];

  /** Methods **/

  /**
   * Updates a form value. Designed so that, in a form field's `onChange` method,
   * you pass this function with the name of the prop you want to update, and it
   * returns a function that will automatically handle saving the returned value.
   *
   * @param key Name of the form prop to update.
   */
  const updateFormValue = (field: string) => (value: unknown) => {
    setFormValues((prev) => {
      const updates: Partial<CreateAdvanceForm> = { [field]: value };

      // Clear dependent fields
      if (field === 'loan') {
        updates.asset = undefined;
        updates.amount = undefined;
      } else if (field === 'asset') {
        // Reset amount when asset changes to avoid invalid calculations
        updates.amount = undefined;
      }

      return { ...prev, ...updates };
    });

    // Clear error when field is updated
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  /**
   * Validates the form data. If errors are detected, it saves them to `formErrors` and returns false.
   *
   * @returns Boolean indicating whether the data is valid
   */
  const validateForm = () => {
    const errors: FormErrors = {};

    if (!formValues.loan) errors.loan = 'Loan is required';
    if (formValues.loan?.isOwned && !formValues?.asset) errors.asset = 'Asset is required';
    if (!formValues.name) errors.name = 'Name is required';
    if (!isNumber(formValues.amount)) errors.amount = 'Valid amount is required';
    if (!formValues.dateFulfilled) errors.dateFulfilled = 'Date is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Creates an Advance.
   *
   * @remarks
   * Redirects to the new Advance's Detail page afterwards.
   */
  const create = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors first.');
      return;
    }

    try {
      const response = await advanceStore.create({
        ...formValues,
        originationFee: parseFloat(formValues.amount || '0') * (formValues.loan?.originationFee ?? 0),
      });
      toast.success(toastMessages.createAdvance.success);
      navigate(`/advances/${response._id}`);
    } catch (err) {
      console.error(err);
      toast.error(toastMessages.createAdvance.error);
    }
  };

  /** Effects **/
  useEffect(() => {
    if (formValues.loan) updateFormValue('company')(formValues.loan.borrower);
  }, [formValues.loan]);

  useEffect(() => {
    if (assetStore.currentAsset) {
      const asset = assetStore.currentAsset;
      updateFormValue('asset')({
        ...(formValues.asset ?? asset),
        expectedResidualPayment: asset.expectedResidualPayment,
      });
      setIsLoaded(true);
    }
  }, [assetStore.currentAsset]);

  /** Render **/
  return (
    <CreatePage modelType={DataModelType.Advance} onCreate={create}>
      <LoanSelect
        value={formValues.loan?._id ?? ''}
        setValue={(loanId) => updateFormValue('loan')(filteredLoans.find((loan) => loan._id === loanId))}
        loans={filteredLoans}
        error={formErrors.loan}
      />

      {formValues.loan && formValues.loan.isOwned && (
        <AssetSelect
          value={formValues.asset?._id ?? ''}
          setValue={(assetId: TAssetId) => assetStore.get(assetId)} // this will cause the form to auto-update
          assets={filteredAssets}
          error={formErrors.asset}
        />
      )}

      <Box display="flex">
        <Box display="flex" flexDirection="column" flex={1}>
          <FuseInput
            id="name"
            label="Advance Name"
            value={formValues.name}
            updateValue={updateFormValue('name')}
            required
            error={formErrors.name}
          />

          <FuseInput
            id="amount"
            label="Amount"
            value={formValues.amount}
            updateValue={updateFormValue('amount')}
            type="number"
            moneyMask
            negativeNumber
            required
            error={formErrors.amount}
          />
        </Box>

        <Box display="flex" flexDirection="column" flex={1}>
          {!formValues.loan && (
            <Box display="flex" justifyContent="center" alignItems="center" className="md-title thin" height="100%">
              Please Select a Deal.
            </Box>
          )}

          {formValues.loan && !formValues.loan.isOwned && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              className="md-title"
              height="100%"
            >
              <Box paddingBottom="6px" marginBottom="6px" borderBottom="solid 1px rgba(0,0,0,0.12)" className="thin">
                Available Credit
              </Box>
              <Box id="currentValue">
                {formValues.loan.assetValue
                  ? currency.format(
                      formValues.loan.assetValue * formValues.loan.advanceRate - formValues.loan.principalBalance
                    )
                  : '-'}
              </Box>
            </Box>
          )}

          {formValues.loan && formValues.loan.isOwned && !formValues.asset && (
            <Box display="flex" justifyContent="center" alignItems="center" className="md-title thin" height="100%">
              Please Select an Asset.
            </Box>
          )}

          {formValues.loan?.isOwned && formValues.asset && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              className="md-title thin"
              height="100%"
              flexDirection="column"
              gap="16px"
            >
              <Box display="flex" width="100%">
                <Box
                  flex={50}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  className="md-title"
                  height="100%"
                >
                  <Box
                    paddingBottom="6px"
                    marginBottom="6px"
                    borderBottom="solid 1px rgba(0,0,0,0.12)"
                    className="thin"
                  >
                    Asset Value
                  </Box>
                  {formValues.asset?.originalValue && (
                    <Box id="originalValue">{currency.format(formValues.asset?.originalValue)}</Box>
                  )}
                  {!formValues.asset?.originalValue && (
                    <Box className="md-caption center thin">
                      <strong>No Asset value found.</strong>
                    </Box>
                  )}
                </Box>
                <Box
                  flex={50}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  className="md-title"
                  height="100%"
                >
                  <Box
                    paddingBottom="6px"
                    marginBottom="6px"
                    borderBottom="solid 1px rgba(0,0,0,0.12)"
                    className="thin"
                  >
                    Initial Payment Amount
                  </Box>
                  {formValues.asset?.initialPaymentAmount && (
                    <Box id="initialPaymentAmount">{currency.format(formValues.asset?.initialPaymentAmount)}</Box>
                  )}
                  {!formValues.asset?.initialPaymentAmount && (
                    <Box className="md-caption center thin">
                      <strong>No initial payment amount found.</strong>
                    </Box>
                  )}
                </Box>
              </Box>

              <Box display="flex" width="100%">
                <Box
                  flex={50}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  className="md-title"
                  height="100%"
                >
                  <Box
                    paddingBottom="6px"
                    marginBottom="6px"
                    borderBottom="solid 1px rgba(0,0,0,0.12)"
                    className="thin"
                  >
                    Socket Fees
                  </Box>
                  <Box id="socketFees">
                    {currency.format(
                      (formValues.asset.adminFee || 0) +
                        (formValues.asset.discountFee || 0) +
                        (formValues.asset.lateFee || 0)
                    )}
                  </Box>
                </Box>

                <Box
                  flex={50}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  className="md-title"
                  height="100%"
                >
                  <Box
                    paddingBottom="6px"
                    marginBottom="6px"
                    borderBottom="solid 1px rgba(0,0,0,0.12)"
                    className="thin"
                  >
                    Expected Residual Payment
                  </Box>
                  {typeof formValues.asset.expectedResidualPayment === 'number' && (
                    <Box id="expectedResidualPayment">{currency.format(formValues.asset.expectedResidualPayment)}</Box>
                  )}
                  {!isLoaded && (
                    <Box className="md-caption center thin">
                      <strong>Calculating...</strong>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" gap="6px">
        <FuseInput
          label="Date Requested"
          value={formValues.dateRequested}
          updateValue={updateFormValue('dateRequested')}
          type="date"
          required
          error={formErrors.dateRequested}
          // flex="50"
        />
        <FuseInput
          label="Date Fulfilled"
          value={formValues.dateFulfilled}
          updateValue={updateFormValue('dateFulfilled')}
          type="date"
          required
          error={formErrors.dateFulfilled}
          // flex="50"
        />
      </Box>
    </CreatePage>
  );
});
